import { useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import ButtonIcon from "ds/components/ButtonIcon";
import { Pin, PinFilled } from "components/icons/generated";

import { MainNavigationPrimaryMenuItemKeys } from "../../../config/menuItemsPrimary";
import { type MainNavigationMenuItem } from "../../../types";
import useMainNavigationStore from "../../../stores/useMainNavigationStore";

type MainNavigationPinActionProps = {
  item: MainNavigationMenuItem;
  size?: "small" | "medium";
  variant?: "ghost" | "primary" | "secondary";
  children?: string;
  className?: string;
  onPin?: (itemId: string) => void;
  onUnpin?: (itemId: string) => void;
};

const MainNavigationPinAction = ({
  item,
  size = "small",
  variant = "ghost",
  children,
  className,
  onPin,
  onUnpin,
}: MainNavigationPinActionProps) => {
  const { pinnedItemIds, pinMenuItem, unpinMenuItem, clearRevealedPin } = useMainNavigationStore(
    useShallow((state) => ({
      pinnedItemIds: state.pinnedItemIds,
      pinMenuItem: state.pinMenuItem,
      unpinMenuItem: state.unpinMenuItem,
      clearRevealedPin: state.clearRevealedPin,
    }))
  );

  const isPinned = pinnedItemIds.includes(item.id);

  const handleTogglePin = useCallback(() => {
    clearRevealedPin();

    if (isPinned) {
      unpinMenuItem(item.id);
      onUnpin?.(item.id);
    } else {
      pinMenuItem(item.id);
      onPin?.(item.id);
    }
  }, [clearRevealedPin, isPinned, item.id, onPin, onUnpin, pinMenuItem, unpinMenuItem]);

  return (
    <ButtonIcon
      size={size}
      variant={variant}
      icon={isPinned ? PinFilled : Pin}
      onPress={handleTogglePin}
      className={className}
      data-userflow-id={
        item.id === MainNavigationPrimaryMenuItemKeys.Launchpad
          ? `${isPinned ? "unpin" : "pin"}-${item.id}`
          : undefined
      }
    >
      {children || (isPinned ? "Unpin" : "Pin")}
    </ButtonIcon>
  );
};

export default MainNavigationPinAction;
